/**
 * Watches a peer connection for video that has stopped arriving.
 *
 * The connection state alone does not say whether the stream is alive. After a
 * laptop sleeps, the device has long since dropped the peer (its ICE consent
 * expires ~30 s in), yet on wake the browser keeps reporting "connected" over a
 * black frame until its own ICE timers catch up — and once they do, "failed" is
 * where it stays. Received bytes are the ground truth: the device sends at
 * least one frame per second, so a few seconds of silence is never a quiet
 * camera.
 *
 * The clock starts when the connection first reports "connected", so a slow
 * handshake is not mistaken for a stall.
 */
export interface StallWatchOptions {
  /** Silence that counts as a dead stream. */
  timeoutMs: number;
  intervalMs?: number;
  /** True while silence is expected (a replay that has finished). */
  isIdle: () => boolean;
  /** Video arrived since the last check. */
  onProgress: () => void;
  /** Called once; the watch stops itself first. */
  onStall: () => void;
}

export const watchForStall = (
  pc: RTCPeerConnection,
  {
    timeoutMs,
    intervalMs = 1000,
    isIdle,
    onProgress,
    onStall,
  }: StallWatchOptions,
): (() => void) => {
  let stopped = false;
  let connected = false;
  let lastBytes = 0;
  let lastProgressAt = 0;
  let lastTickAt = performance.now();

  const tick = async () => {
    const now = performance.now();
    // A tick far later than scheduled means this page wasn't running — the
    // machine slept, or a background tab was throttled. Nothing was watched in
    // the gap, so restart the clock rather than count it as silence; a stream
    // that survived the sleep gets the full timeout to show it.
    const resumed = now - lastTickAt > intervalMs * 3;
    lastTickAt = now;

    if (!connected) {
      if (pc.connectionState !== "connected") return;
      connected = true;
      lastProgressAt = now;
    }
    if (resumed || isIdle()) {
      lastProgressAt = now;
      return;
    }

    let bytes: number;
    try {
      bytes = await receivedVideoBytes(pc);
    } catch {
      return;
    }
    if (stopped) return;

    if (bytes > lastBytes) {
      lastBytes = bytes;
      lastProgressAt = now;
      onProgress();
    } else if (now - lastProgressAt >= timeoutMs) {
      stop();
      onStall();
    }
  };

  const id = setInterval(() => void tick(), intervalMs);
  const stop = () => {
    stopped = true;
    clearInterval(id);
  };
  return stop;
};

const receivedVideoBytes = async (pc: RTCPeerConnection): Promise<number> => {
  const stats = await pc.getStats();
  let bytes = 0;
  stats.forEach((report) => {
    if (report.type === "inbound-rtp" && report.kind === "video") {
      bytes += report.bytesReceived ?? 0;
    }
  });
  return bytes;
};
